import React from 'react';
import { Link } from 'react-router-dom';
import { SpeakerWaveIcon, BookOpenIcon, ChartBarIcon, CheckIcon, ArrowRightIcon, AcademicCapIcon, ClockIcon } from '@heroicons/react/24/outline';
import MainNavbar from '../components/MainNavbar';
import ScrollAnimation from '../components/hero/ScrollAnimation';
import GlassyIcon from '../components/common/GlassyIcon';

import { OptimizedImage } from '../utils/imageManager';

const StepCard = ({ number, icon: Icon, title, description, highlight }) => (
  <div className="relative h-full">
    {/* Number badge positioned outside the card */}
    <div
      className={`absolute -top-4 left-8 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold backdrop-blur-sm border-2 z-20 ${
        highlight ? 'bg-white/20 text-primary border-white' : 'bg-primary text-white border-primary'
      }`}
      style={{
        background: highlight ? 'rgba(255, 255, 255, 0.9)' : undefined,
      }}
    >
      {number}
    </div>

    <div
      className={`relative p-4 sm:p-6 md:p-8 pt-6 sm:pt-8 md:pt-10 rounded-2xl backdrop-blur-sm border-2 border-border overflow-hidden h-full ${
        highlight ? 'text-white bg-primary' : 'text-content-primary bg-background-secondary'
      }`}
      style={{
        boxShadow: highlight ? '0 8px 32px var(--color-primary)30' : undefined,
      }}
    >
      {/* Glassmorphic overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${
          highlight ? 'from-white/30 to-transparent opacity-70' : 'from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30'
        }`}
      />

      {/* Audio bars pattern - adjusted for mobile */}
      <div className="absolute inset-0 opacity-20 dark:opacity-15">
        {/* Top-right corner audio bars - reduced translate on mobile */}
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex gap-1">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: '3px',
                height: `${10 + i * 3}px`,
                background: highlight ? 'white' : 'var(--color-primary)',
                animation: `audio-bar ${2 + i * 0.3}s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>

        {/* Bottom-left corner audio bars - reduced translate on mobile */}
        <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 flex gap-1">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: '2px',
                height: `${8 + i * 2}px`,
                background: highlight ? 'white' : 'var(--color-primary)',
                animation: `audio-bar ${2.4 + i * 0.3}s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Icon container */}
      <div className="relative z-10 mb-4 sm:mb-6">
        {highlight ? (
          <GlassyIcon icon={Icon} size="md" color="white" className="border-0 sm:w-12 sm:h-12 [&>svg]:text-primary" />
        ) : (
          <GlassyIcon icon={Icon} size="md" color="orange" className="sm:w-12 sm:h-12" />
        )}
      </div>

      {/* Content */}
      <div className="relative z-10">
        <h3 className={`text-lg sm:text-xl font-bold mb-2 sm:mb-3 drop-shadow-sm ${highlight ? 'text-white' : 'text-content-primary'}`}>{title}</h3>
        <p className={`text-sm sm:text-base leading-relaxed drop-shadow-sm ${highlight ? 'text-white/90' : 'text-content-secondary'}`}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

const FeatureHighlight = ({ imageKey, title, description, benefits, imageFirst }) => (
  <div className={`flex flex-col lg:flex-row items-center gap-8 sm:gap-12 lg:gap-16 ${imageFirst ? 'lg:flex-row-reverse' : ''}`}>
    {/* Image Container */}
    <div className="w-full lg:w-1/2">
      <div className="relative group max-w-md mx-auto lg:max-w-none">
        {/* Background glow */}
        <div
          className="absolute inset-0 rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300 bg-gradient-to-br from-primary to-secondary"
          style={{
            transform: 'scale(1.05)', // Reduced scale to prevent overflow
          }}
        />
        {/* Glass container */}
        <div className="relative rounded-3xl backdrop-blur-sm border-2 border-border p-4 sm:p-6 overflow-hidden transform hover:scale-105 transition-transform duration-300 bg-background-secondary">
          {/* Glassmorphic overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

          {/* Consistent size bubbles - adjusted for mobile */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full translate-x-4 sm:translate-x-8 -translate-y-4 sm:-translate-y-8 bg-primary" />
            <div className="absolute bottom-0 left-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full -translate-x-3 sm:-translate-x-6 translate-y-3 sm:translate-y-6 bg-secondary" />
          </div>

          <OptimizedImage 
            imageKey={imageKey} 
            className="relative z-10 w-full drop-shadow-lg" 
          />
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="w-full lg:w-1/2 space-y-4 sm:space-y-6 px-4 sm:px-0">
      <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-content-primary leading-tight drop-shadow-sm">{title}</h3>
      <p className="text-base sm:text-lg text-content-secondary leading-relaxed">{description}</p>
      <div className="space-y-3">
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-start gap-3">
            <GlassyIcon icon={CheckIcon} size="sm" color="green" className="w-6 h-6 mt-0.5" />
            <span className="text-content-secondary text-sm sm:text-base">{benefit}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ValueCard = ({ icon: Icon, title, description }) => (
  <div className="relative p-4 sm:p-6 rounded-xl backdrop-blur-sm border-2 border-border overflow-hidden h-full bg-background-secondary">
    {/* Glassmorphic overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

    {/* Consistent size bubbles - adjusted for mobile */}
    <div className="absolute inset-0 opacity-15">
      <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full translate-x-4 sm:translate-x-8 -translate-y-4 sm:-translate-y-8 bg-primary" />
      <div className="absolute bottom-0 left-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full -translate-x-3 sm:-translate-x-6 translate-y-3 sm:translate-y-6 bg-secondary" />
    </div>

    <div className="relative z-10 text-center">
      <div className="mb-3 sm:mb-4 flex justify-center">
        <GlassyIcon icon={Icon} size="md" color="orange" />
      </div>
      <h3 className="text-base sm:text-lg font-semibold text-content-primary mb-2 drop-shadow-sm">{title}</h3>
      <p className="text-xs sm:text-sm text-content-secondary">{description}</p>
    </div>
  </div>
);

const MobileValueProp = ({ icon: Icon, title }) => (
  <div className="flex flex-col items-center gap-2 text-center">
    <GlassyIcon icon={Icon} size="sm" color="orange" />
    <span className="text-xs font-semibold text-content-primary">{title}</span>
  </div>
);

function MainPage() {
  return (
    <div className="bg-background-primary overflow-x-hidden">
      {' '}
      {/* Added overflow-x-hidden */}
      <MainNavbar />
      <main className="overflow-x-hidden">
        {' '}
        {/* Added overflow-x-hidden */}
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background pattern - adjusted for mobile */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 sm:w-96 sm:h-96 rounded-full blur-3xl bg-primary" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 sm:w-96 sm:h-96 rounded-full blur-3xl bg-secondary" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 lg:gap-x-16 gap-y-8 sm:gap-y-12 items-center">
              {/* Headline & Paragraph */}
              <div className="text-center lg:text-left">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-content-primary leading-tight mb-4 sm:mb-6 drop-shadow-sm">
                  From <span className="text-primary">Confusion</span> to <span className="text-secondary">Comprehension</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-content-secondary leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  Transform your Japanese listening skills with our effective learning system. Master real conversations through native audio and
                  smart spaced repetition.
                </p>
              </div>

              {/* Mascot Image */}
              <div className="lg:col-start-2 lg:row-start-1 lg:row-span-3 flex items-center justify-center">
                <div className="relative w-3/4 sm:w-1/2 lg:w-full max-w-sm lg:max-w-none">
                  {/* Glow effect - reduced for mobile */}
                  <div
                    className="absolute inset-0 rounded-full blur-xl sm:blur-2xl opacity-30 bg-gradient-to-br from-primary to-secondary"
                    style={{
                      transform: 'scale(1.1)', // Reduced scale
                    }}
                  />
                  <OptimizedImage
                    imageKey="HOME_0"
                    className="relative mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>

              {/* Value Props */}
              <div className="w-full">
                {/* Mobile-only compact view */}
                <div className="sm:hidden flex justify-around gap-2 px-4">
                  <MobileValueProp icon={AcademicCapIcon} title="Free to Start" />
                  <MobileValueProp icon={ClockIcon} title="Smart SRS" />
                  <MobileValueProp icon={SpeakerWaveIcon} title="Native Audio" />
                </div>

                {/* Desktop-only expanded view */}
                <div className="hidden sm:grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto lg:mx-0">
                  <ValueCard icon={AcademicCapIcon} title="Free to Start" description="Begin with N5 content at no cost" />
                  <ValueCard icon={ClockIcon} title="Smart SRS" description="Optimized review scheduling" />
                  <ValueCard icon={SpeakerWaveIcon} title="Native Audio" description="Learn with authentic voices" />
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-md mx-auto sm:max-w-none justify-center lg:justify-start">
                <Link
                  to="/user/register"
                  className="relative w-full sm:w-auto group inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl backdrop-blur-sm border-2 border-transparent overflow-hidden text-base sm:text-lg font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl text-white bg-primary"
                  style={{
                    boxShadow: '0 8px 32px var(--color-primary)30',
                  }}
                >
                  {/* Glassmorphic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />

                  <span className="relative z-10 drop-shadow-sm">Start Learning FREE</span>
                  <ArrowRightIcon className="relative z-10 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform drop-shadow-sm" />
                </Link>

                <Link
                  to="/pricing"
                  className="relative w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl backdrop-blur-sm border-2 border-secondary text-base sm:text-lg font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-xl overflow-hidden group bg-background-secondary text-secondary"
                >
                  {/* Glassmorphic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 group-hover:opacity-70 transition-opacity duration-300" />

                  <span className="relative z-10">View Pricing</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
        {/* How It Works Section */}
        <section className="py-12 sm:py-16 lg:py-32 relative overflow-hidden bg-background-secondary">
          {/* Background pattern - adjusted for mobile */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-48 h-48 sm:w-72 sm:h-72 rounded-full blur-3xl bg-secondary" />
            <div className="absolute bottom-0 right-0 w-48 h-48 sm:w-72 sm:h-72 rounded-full blur-3xl bg-primary" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation>
              <div className="text-center mb-12 sm:mb-16">
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-content-primary mb-3 sm:mb-4 drop-shadow-sm">Your Learning Journey</h2>
                <p className="text-base sm:text-lg lg:text-xl text-content-secondary max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
                  Our proven 3-step method transforms how you understand spoken Japanese. Simple, effective, and designed for real-world success.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3 max-w-6xl mx-auto px-4 sm:px-0">
              <ScrollAnimation delay={0} className="h-full">
                <StepCard
                  number="1"
                  icon={SpeakerWaveIcon}
                  title="Listen to Real Japanese"
                  description="Immerse yourself in authentic sentences spoken by native speakers. Start with your level and progressively challenge yourself."
                />
              </ScrollAnimation>
              <ScrollAnimation delay={200} className="h-full">
                <StepCard
                  number="2"
                  icon={BookOpenIcon}
                  title="Break Down Unknown Words"
                  description="Instantly isolate and learn any word you don't understand. Build your vocabulary within meaningful context."
                  highlight
                />
              </ScrollAnimation>
              <ScrollAnimation delay={400} className="h-full">
                <StepCard
                  number="3"
                  icon={ChartBarIcon}
                  title="Master with Smart Reviews"
                  description="Our spaced repetition system ensures you review at the perfect moment for long-term retention."
                />
              </ScrollAnimation>
            </div>
          </div>
        </section>
        {/* Features Section */}
        <section className="py-12 sm:py-16 lg:py-32 relative overflow-hidden">
          {/* Background pattern - adjusted for mobile */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-1/3 left-1/3 w-64 h-64 sm:w-96 sm:h-96 rounded-full blur-3xl bg-primary" />
            <div className="absolute bottom-1/3 right-1/3 w-64 h-64 sm:w-96 sm:h-96 rounded-full blur-3xl bg-secondary" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 sm:space-y-16 lg:space-y-24">
            <ScrollAnimation>
              <FeatureHighlight
                imageKey="HOME_1"
                title="Context-Rich Learning"
                description="Forget boring flashcards. Learn words as they're actually used in conversation. Every word is linked to real sentences, revealing grammar and natural intonation."
                benefits={['Learn from real native sentences', 'Acquire grammar patterns naturally', 'Build vocabulary in authentic context']}
              />
            </ScrollAnimation>
            <ScrollAnimation>
              <FeatureHighlight
                imageKey="HOME_2"
                title="Learn Together, Grow Faster"
                description="Join our growing community of Japanese learners. Share progress, exchange tips, and motivate each other on your language journey."
                benefits={['Connect with fellow learners', 'Share effective learning strategies', 'Find motivation and support']}
                imageFirst
              />
            </ScrollAnimation>
            <ScrollAnimation>
              <FeatureHighlight
                imageKey="HOME_3"
                title="Track Every Victory"
                description="Watch your Japanese skills grow with detailed progress tracking. See your improvements across all JLPT levels and stay motivated with clear visual feedback."
                benefits={['Monitor progress by JLPT level', 'Analyze your personal learning trends', 'Celebrate milestones and achievements']}
              />
            </ScrollAnimation>
          </div>
        </section>
        {/* Unified CTA Section */}
        <section className="py-12 sm:py-16 lg:py-32 relative overflow-hidden bg-gradient-to-br from-primary to-secondary">
          {/* Glassmorphic overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70" />

          {/* Audio bars pattern - adjusted positioning for mobile */}
          <div className="absolute inset-0 opacity-20">
            {/* Multiple audio bar groups */}
            <div className="absolute top-4 left-4 sm:top-8 sm:left-8 flex gap-1">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-full bg-white"
                  style={{
                    width: '4px',
                    height: `${12 + i * 4}px`,
                    animation: `audio-bar ${2 + i * 0.2}s ease-in-out infinite alternate`,
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
            <div className="absolute top-8 right-8 sm:top-16 sm:right-16 flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-full bg-white"
                  style={{
                    width: '3px',
                    height: `${10 + i * 3}px`,
                    animation: `audio-bar ${1.8 + i * 0.25}s ease-in-out infinite alternate`,
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
            <div className="absolute bottom-6 left-10 sm:bottom-12 sm:left-20 flex gap-1">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-full bg-white"
                  style={{
                    width: '3px',
                    height: `${8 + i * 2}px`,
                    animation: `audio-bar ${2.2 + i * 0.3}s ease-in-out infinite alternate`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </div>
          </div>

          <ScrollAnimation>
            <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-sm">
                Ready to Understand
                <span className="block">Real Japanese?</span>
              </h2>
              <p className="text-base sm:text-xl text-white/90 mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto drop-shadow-sm">
                Start with N5 sentences and words completely free. No credit card required. Just pure, effective learning.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
                <Link
                  to="/user/register"
                  className="relative w-full sm:w-auto group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 rounded-xl backdrop-blur-sm border-2 border-white/20 text-base sm:text-lg font-bold transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl overflow-hidden bg-white text-primary"
                >
                  {/* Glassmorphic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 group-hover:opacity-70 transition-opacity duration-300" />

                  <span className="relative z-10 drop-shadow-sm">Start Your Free Journey</span>
                  <ArrowRightIcon className="relative z-10 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform drop-shadow-sm" />
                </Link>

                <Link
                  to="/pricing"
                  className="relative w-full sm:w-auto inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl backdrop-blur-sm border-2 border-white/40 text-white text-base sm:text-lg font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-xl overflow-hidden group"
                >
                  {/* Glassmorphic overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-30 group-hover:opacity-50 transition-opacity duration-300" />

                  <span className="relative z-10 drop-shadow-sm">View All Plans</span>
                </Link>
              </div>
            </div>
          </ScrollAnimation>
        </section>
      </main>
    </div>
  );
}

export default MainPage;
